import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase, unwrap } from '@/lib/supabase';

export const tasksKey = ['tasks'] as const;
export const projectsKey = ['projects'] as const;

export const useTasks = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: tasksKey,
    queryFn: async () =>
      unwrap(await supabase.from('tasks').select('*, subtasks(*)').order('sort_order')),
  });

export const useProjects = () =>
  useQuery({
    queryKey: projectsKey,
    queryFn: async () => unwrap(await supabase.from('projects').select('*').order('created_at')),
  });

export type Task = NonNullable<ReturnType<typeof useTasks>['data']>[number];
export type Project = NonNullable<ReturnType<typeof useProjects>['data']>[number];
export type Subtask = Task['subtasks'][number];
export type TaskPatch = Partial<
  Pick<
    Task,
    | 'title'
    | 'status'
    | 'sort_order'
    | 'due_date'
    | 'owner_id'
    | 'priority'
    | 'project_id'
    | 'notes'
  >
>;

const UNDO_MS = 6000;

/** Writes show at once on every view; a failed write puts the old list back. */
export function useTaskActions() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setTasks = (fn: (old: Task[]) => Task[]) =>
    qc.setQueryData<Task[]>(tasksKey, (old) => (old ? fn(old) : old));
  const optimistic = async (fn: (old: Task[]) => Task[]) => {
    await qc.cancelQueries({ queryKey: tasksKey });
    const prev = qc.getQueryData<Task[]>(tasksKey);
    setTasks(fn);
    return { prev };
  };
  const rollback = (_e: unknown, _v: unknown, ctx?: { prev?: Task[] }) =>
    ctx?.prev && qc.setQueryData(tasksKey, ctx.prev);
  const settle = () => qc.invalidateQueries({ queryKey: tasksKey });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: TaskPatch }) =>
      unwrap(await supabase.from('tasks').update(patch).eq('id', id)),
    onMutate: ({ id, patch }) =>
      optimistic((old) => old.map((x) => (x.id === id ? { ...x, ...patch } : x))),
    onError: rollback,
    onSettled: settle,
  });

  const subtask = useMutation({
    mutationFn: async (
      op:
        | { kind: 'add'; taskId: string; title: string; position: number }
        | { kind: 'update'; id: string; patch: Partial<Pick<Subtask, 'title' | 'done'>> }
        | { kind: 'remove'; id: string },
    ) => {
      if (op.kind === 'add')
        unwrap(
          await supabase
            .from('subtasks')
            .insert({ task_id: op.taskId, title: op.title, position: op.position }),
        );
      else if (op.kind === 'update')
        unwrap(await supabase.from('subtasks').update(op.patch).eq('id', op.id));
      else unwrap(await supabase.from('subtasks').delete().eq('id', op.id));
    },
    onMutate: (op) =>
      optimistic((old) =>
        old.map((x) => {
          if (op.kind === 'add')
            return x.id === op.taskId
              ? {
                  ...x,
                  subtasks: [
                    ...x.subtasks,
                    {
                      id: `tmp-${op.position}`,
                      task_id: op.taskId,
                      title: op.title,
                      done: false,
                      position: op.position,
                    },
                  ],
                }
              : x;
          return {
            ...x,
            subtasks:
              op.kind === 'update'
                ? x.subtasks.map((s) => (s.id === op.id ? { ...s, ...op.patch } : s))
                : x.subtasks.filter((s) => s.id !== op.id),
          };
        }),
      ),
    onError: rollback,
    onSettled: settle,
  });

  /**
   * The task disappears at once and is deleted only when the undo toast closes, so "undo" needs
   * no re-insert.
   */
  const remove = (task: Task) => {
    const prev = qc.getQueryData<Task[]>(tasksKey);
    setTasks((old) => old.filter((x) => x.id !== task.id));
    // set by undo, or by the first of onAutoClose/onDismiss, so the delete runs at most once
    let settled = false;
    const commit = async () => {
      if (settled) return;
      settled = true;
      try {
        unwrap(await supabase.from('tasks').delete().eq('id', task.id));
      } catch (e) {
        if (prev) qc.setQueryData(tasksKey, prev);
        toast.error(e instanceof Error ? e.message : String(e));
      }
      void settle();
    };
    toast(t('tasks.deleted', { title: task.title }), {
      duration: UNDO_MS,
      action: {
        label: t('common.undo'),
        onClick: () => {
          settled = true;
          void settle();
        },
      },
      onAutoClose: () => void commit(),
      onDismiss: () => void commit(),
    });
  };

  return { update, subtask, remove };
}
